// A pénztári kontír-szerkesztő ("Adott pénztárkivonat kontírozási tételei",
// K2D w_popup_cash_accitem_os) modelljei. A tétel az imacccashaccitem sor képe,
// a bontás az imacccashaccit_i_d soré — a dimCode SZÖVEGES kód (nvarchar(10)).

export interface CashPreAccEditDimension {
  id: number;            // szam; <= 0 = új (negatív temp id)
  head: number;          // kontírtétel szam (mindig létező, pozitív id)
  dimCodeType: number;   // csaccdimcodetype: 5 = IV. ... 13 = XII.
  dimCode: string;
  total: number;

  extProdParts: number;
  extProdPartsCode: string;
  extProdPartsName: string;
  productParts: number;
  productPartsCode: string;
  productPartsName: string;
  production: number;
  stock: number;
  carId: number;
  regNum: string;
  userId: number;
  userName: string;
  stationId: number;
  stationName: string;
  toolId: number;
  toolName: string;
  project: number;
  projectName: string;
  projectMainCode: string;
  projectCode: string;
  poId: number;
  poNumber: string;
  workNumber: string;
  extProdPartner: number;
  extProdPartnerName: string;

  inEdit: boolean;
  selected: boolean;
  deleting: boolean;
}

export const CashPreAccEditDimensionDefaultProps: CashPreAccEditDimension = {
  id: 0,
  head: 0,
  dimCodeType: 5,
  dimCode: "",
  total: 0,

  extProdParts: 1,
  extProdPartsCode: "",
  extProdPartsName: "",
  productParts: 1,
  productPartsCode: "",
  productPartsName: "",
  production: 1,
  stock: 1,
  carId: 1,
  regNum: "",
  userId: 1,
  userName: "",
  stationId: 1,
  stationName: "",
  toolId: 1,
  toolName: "",
  project: 1,
  projectName: "",
  projectMainCode: "",
  projectCode: "",
  poId: 1,
  poNumber: "",
  workNumber: "",
  extProdPartner: 1,
  extProdPartnerName: "",

  inEdit: false,
  selected: false,
  deleting: false,
};

// Egy kontírtétel (K2D d_g_i_cashaccitem_os sora). K2D-hűen csak az
// accCode / detailedType / dimCode1-3 / note szerkeszthető; tétel felvétele
// és törlése NINCS — sort csak a kontír-motor (f_cash_cont_os port) generál.
export interface CashPreAccEditItem {
  id: number;
  document: number;
  docitem: number;
  accCode: string;
  accCodeName: string;
  accSign: string;       // '+' = Tartozik, '-' = Követel
  detailedType: number;
  total: number;
  currency: string;
  accDate: string;
  note: string;
  dimCode1: string;
  dimCode2: string;
  dimCode3: string;
  invCode: string;
  prefix: string;        // a hivatkozott számla magyar prefixe
  invNo: number;
  outerInvNo: string;
  payment: number;
  period: number;
  cashProcType: number;
  cashProcTypeName: string;
  partnerName: string;
  income: boolean;       // a bizonylat bevétel-e (K2D total-háttérszín)

  inEdit: boolean;
  selected: boolean;
}

export const CashPreAccEditItemDefaultProps: CashPreAccEditItem = {
  id: 0,
  document: 0,
  docitem: 0,
  accCode: "",
  accCodeName: "",
  accSign: "+",
  detailedType: 0,
  total: 0,
  currency: "HUF",
  accDate: "",
  note: "",
  dimCode1: "",
  dimCode2: "",
  dimCode3: "",
  invCode: "",
  prefix: "",
  invNo: 0,
  outerInvNo: "",
  payment: 0,
  period: 1,
  cashProcType: 1,
  cashProcTypeName: "",
  partnerName: "",
  income: false,

  inEdit: false,
  selected: false,
};

// A szerkesztő read-only fej-panelje (K2D d_f_cash_os)
export interface CashPreAccEditorHead {
  id: number;
  status: number;
  statusName: string;
  accStatus: number;
  accStatusName: string;
  cashPointName: string;
  ownIdentifier: string;
  accNo: number;
  extractDate: string;
  extractTime: string;
  openBal: number;
  closeBal: number;
  currency: string;
  income: number;   // K2D bevetel
  expense: number;  // K2D kiadas
}

export const CashPreAccEditorHeadDefaultProps: CashPreAccEditorHead = {
  id: 0,
  status: 1,
  statusName: "",
  accStatus: 1,
  accStatusName: "",
  cashPointName: "",
  ownIdentifier: "",
  accNo: 0,
  extractDate: "",
  extractTime: "",
  openBal: 0,
  closeBal: 0,
  currency: "HUF",
  income: 0,
  expense: 0,
};

// A kivonat bizonylattételei (imacccashdocument status=4 + imacccashdocitem)
// az egyenleg-panelhez — a pénztárnál az egyenleg-ellenőrzés tétel-szintű
export interface CashPreAccDocItem {
  id: number;
  document: number;
  partnerName: string;
  amount: number;
  income: boolean;
  cashProcTypeName: string;
}

export interface CashPreAccAccNo {
  id: number;
  accNo: string;
  name: string;
  mainTypeName: string;
  detailedType: number;
}

export interface CashPreAccDimCode {
  id: number;
  dimcode: string;
  national: string;
  type: number;
}

export interface CashPreAccEditorFlags {
  extBook: boolean;
  dim49: boolean;
  checkDetType: boolean;
  // K2D pssyssysfile1.is_up_changeable — a "Feladás tételei" gomb láthatósága
  upChangeable: boolean;
  dimTypeLive: Record<number, boolean>;
  // csaccdimcodetype.required — a K2D f_dimcode_check_os csak ezeken kényszeríti ki a dimkódot
  dimTypeRequired: Record<number, boolean>;
}

export interface CashPreAccEditorData {
  head: CashPreAccEditorHead;
  items: CashPreAccEditItem[];
  dimensions: CashPreAccEditDimension[];
  docItems: CashPreAccDocItem[];
  originalAccStatus: number;
  editable: boolean;       // status=3 és acc_status ∈ {2,5,7,8,9}
  reAccountable: boolean;  // acc_status ∈ {2,3,5,7,8,9}
  uploadable: boolean;     // K2D pb_felad kapu: acc_status ∉ {4,6}
  accNos: CashPreAccAccNo[];
  dimCodes: CashPreAccDimCode[];
  detailedTypes: { id: number; name: string }[];
  flags: CashPreAccEditorFlags;
}

//----------- Mentés (K2D pb_ok) -----------//

export interface CashPreAccSaveRequest {
  originalAccStatus: number;
  items: {
    id: number;
    accCode: string;
    detailedType: number;
    dimCode1: string;
    dimCode2: string;
    dimCode3: string;
    note: string;
  }[];
  dimensions: {
    id: number;
    head: number;
    dimCodeType: number;
    dimCode: string;
    total: number;
    project: number;
    poId: number;
    userId: number;
    stationId: number;
    toolId: number;
    carId: number;
    extProdParts: number;
    extProdPartner: number;
    productParts: number;
    production: number;
    stock: number;
  }[];
  deletedDimensionIds: number[];
}

export interface CashPreAccSaveResult {
  accStatus: number;
  accStatusName: string;
  statusChanged: boolean;
  errors: {
    id: number;
    document: number;
    docItem: number;
    errType: number;
    errTypeName: string;
    note: string;
  }[];
}

// A dim-fülek kísérő-lookupjai
export interface CashPreAccCar {
  id: number;
  reqNum: string;
  bodyNo: string;
  owner: string;
  modell: string;
}

export interface CashPreAccProject {
  id: number;
  mainCode: string;
  code: string;
  name: string;
}

export interface CashPreAccPo {
  id: number;
  workNumber: string;
  poNumber: string;
  project: number;
  extCode: string;
}

export interface CashPreAccTool {
  id: number;
  code: string;
  name: string;
  groupCode: string;
  groupName: string;
}

export interface CashPreAccValidate {
  id: number;
  name: string;
}

//----------- Feladás-tételek (K2D pb_felad → w_popup_item_up_os) -----------//

// A kivonat egy bizonylattétele (imacccashdocitem, a bizonylat status=4) a
// feladás-pipával; az isUp a kontírsorok is_up flagjének K2D-hű aggregátuma
export interface CashPreAccUploadItem {
  id: number;
  document: number;
  partnerName: string;
  amount: number;
  currency: string;
  income: boolean;
  cashProcTypeName: string;
  note: string;
  isUp: boolean;
}

export interface CashPreAccUploadFlagsResult {
  updatedItems: number;
}
